import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw, Check, AlertCircle, Cloud, CloudOff } from 'lucide-react';
import { useOfflineSync } from '../hooks/useOfflineSync';

/**
 * Offline Status Indicator
 * Shows connection status and sync state
 */
const OfflineIndicator = ({ showDetails = false }) => {
  const { isOnline, isSyncing, syncStatus, pendingChanges, forceSync } = useOfflineSync();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    if (!isOnline) {
      setToastMessage('Sin conexión - Los cambios se guardarán localmente');
      setShowToast(true);
    } else if (syncStatus?.success && syncStatus.synced > 0) {
      setToastMessage(`${syncStatus.synced} cambio(s) sincronizado(s)`);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  }, [isOnline, syncStatus]);

  // Minimal indicator for header
  if (!showDetails) {
    return (
      <div className="flex items-center gap-2">
        {/* Connection status */}
        <div 
          className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all ${
            isOnline 
              ? 'bg-green-500/10 text-green-400' 
              : 'bg-yellow-500/10 text-yellow-400'
          }`}
          title={isOnline ? 'Conectado' : 'Sin conexión'}
        >
          {isOnline ? (
            <Wifi className="w-3.5 h-3.5" />
          ) : (
            <WifiOff className="w-3.5 h-3.5" />
          )}
        </div>

        {/* Pending changes indicator */}
        {pendingChanges > 0 && (
          <button
            onClick={forceSync}
            disabled={!isOnline || isSyncing}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all ${
              isSyncing 
                ? 'bg-blue-500/10 text-blue-400' 
                : 'bg-orange-500/10 text-orange-400 hover:bg-orange-500/20'
            }`}
            title={isSyncing ? 'Sincronizando...' : `${pendingChanges} cambio(s) pendiente(s)`}
          >
            {isSyncing ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <>
                <Cloud className="w-3.5 h-3.5" />
                <span>{pendingChanges}</span>
              </>
            )}
          </button>
        )}
      </div>
    );
  }

  // Detailed panel for settings or dashboard
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-white flex items-center gap-2">
          {isOnline ? (
            <Cloud className="w-5 h-5 text-green-400" />
          ) : (
            <CloudOff className="w-5 h-5 text-yellow-400" />
          )}
          Estado de Sincronización
        </h3>
        
        {isOnline && pendingChanges > 0 && (
          <button
            onClick={forceSync}
            disabled={isSyncing}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-500 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        )}
      </div>

      {/* Connection status */}
      <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
        <div className={`p-2 rounded-lg ${isOnline ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
          {isOnline ? (
            <Wifi className="w-5 h-5 text-green-400" />
          ) : (
            <WifiOff className="w-5 h-5 text-yellow-400" />
          )}
        </div>
        <div>
          <div className="text-white font-medium">
            {isOnline ? 'Conectado' : 'Sin Conexión'}
          </div>
          <div className="text-sm text-slate-400">
            {isOnline 
              ? 'Los cambios se sincronizan automáticamente' 
              : 'Los cambios se guardan localmente'
            }
          </div>
        </div>
      </div>

      {/* Pending changes */}
      {pendingChanges > 0 && (
        <div className="flex items-center gap-3 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
          <div className="p-2 bg-orange-500/20 rounded-lg">
            <AlertCircle className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <div className="text-orange-300 font-medium">
              {pendingChanges} cambio(s) pendiente(s)
            </div>
            <div className="text-sm text-orange-400/70">
              {isOnline 
                ? 'Haz clic en sincronizar para guardar en la nube' 
                : 'Se sincronizarán cuando vuelva la conexión'
              }
            </div>
          </div>
        </div>
      )}

      {/* Last sync status */}
      {syncStatus && (
        <div className={`flex items-center gap-3 p-3 rounded-lg ${
          syncStatus.success 
            ? 'bg-green-500/10 border border-green-500/20' 
            : 'bg-red-500/10 border border-red-500/20'
        }`}>
          <div className={`p-2 rounded-lg ${
            syncStatus.success ? 'bg-green-500/20' : 'bg-red-500/20'
          }`}>
            {syncStatus.success ? (
              <Check className="w-5 h-5 text-green-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400" />
            )}
          </div>
          <div>
            <div className={`font-medium ${
              syncStatus.success ? 'text-green-300' : 'text-red-300'
            }`}>
              {syncStatus.success 
                ? `Última sincronización: ${syncStatus.synced} cambio(s)` 
                : 'Error de sincronización'
              }
            </div>
            <div className="text-sm text-slate-400">
              {syncStatus.timestamp?.toLocaleTimeString()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Floating toast notification for offline status
 */
export const OfflineToast = () => {
  const { isOnline, isSyncing, pendingChanges } = useOfflineSync();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setVisible(true);
    } else {
      // Hide after a delay when coming back online
      const timer = setTimeout(() => setVisible(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  if (!visible) return null;

  return (
    <div 
      className={`fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50 transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      <div className={`flex items-center gap-3 p-4 rounded-xl shadow-lg ${
        isOnline 
          ? 'bg-green-600 text-white' 
          : 'bg-slate-800 border border-slate-700 text-white'
      }`}>
        {isOnline ? (
          <>
            <Wifi className="w-5 h-5 flex-shrink-0" />
            <div>
              <div className="font-medium">Conexión restaurada</div>
              {pendingChanges > 0 && (
                <div className="text-sm text-green-100">
                  Sincronizando {pendingChanges} cambio(s)...
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <WifiOff className="w-5 h-5 flex-shrink-0 text-yellow-400" />
            <div>
              <div className="font-medium">Modo sin conexión</div>
              <div className="text-sm text-slate-400">
                Los cambios se guardarán localmente
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Auto-save indicator for contract editor
 */
export const AutoSaveIndicator = ({ isSaving, lastSaved, hasPendingChanges, isOnline }) => {
  return (
    <div className="flex items-center gap-2 text-xs">
      {isSaving ? (
        <span className="flex items-center gap-1 text-blue-400">
          <RefreshCw className="w-3 h-3 animate-spin" />
          Guardando...
        </span>
      ) : hasPendingChanges ? (
        <span className="flex items-center gap-1 text-orange-400">
          <Cloud className="w-3 h-3" />
          Pendiente de sincronizar
        </span>
      ) : lastSaved ? (
        <span className="flex items-center gap-1 text-green-400">
          <Check className="w-3 h-3" />
          Guardado {lastSaved.toLocaleTimeString()}
        </span>
      ) : null}
      
      {!isOnline && (
        <span className="flex items-center gap-1 text-yellow-400 ml-2">
          <WifiOff className="w-3 h-3" />
          Offline
        </span>
      )}
    </div>
  );
};

export default OfflineIndicator;
